import React from "react"
import postcss from "postcss"

export const BLOCKED_ELEMENTS = [
  "iframe",
  "base",
  "template",
  "object",
  "script"
]
export const BLOCKED_ATTRIBUTES = ["on\\w+", "formaction", "action"]

const scopeSelector = (styleString, scope) => {
  const namespaces = Array.isArray(scope) ? scope : [scope]

  try {
    const ast = postcss.parse(styleString)
    ast.walkRules(rule => {
      rule.selectors = rule.selectors.map(selector => {
        return namespaces.reduce((prev, namespace, position) => {
          return position === 0
            ? `.${namespace} ${prev}`
            : `${prev}, .${namespace} ${selector}`
        }, selector)
      })
    })

    return ast.toString()
  } catch {
    const selector = Array.isArray(scope)
      ? scope.map(item => `.${item} $&`).join(", ")
      : `.${scope} $&`
    return styleString.replace(
      /(?:\b[\w ]*)?(?:[\\.#\\[:].+?|\w+[\\.\\[\]#="'~>\w ]*?|\*)(?=\s*?(?:,|\{))(?!.+;$)/gm,
      selector
    )
  }
}

/**
 * Takes a string of HTML and scopes its CSS
 * @param {string} htmlValue
 * @param {string|array} scope
 * @returns {string}
 */
export function scopeStyles(htmlValue, scope = "scoped", wrap = true) {
  if (!htmlValue) return htmlValue

  const domParser = new DOMParser()
  const doc = domParser.parseFromString(htmlValue, "text/html")

  doc.querySelectorAll("style").forEach(style => {
    const styleString = style.innerHTML

    style.remove()

    const scoped = scopeSelector(styleString, scope)

    const newStyle = doc.createElement("style")
    newStyle.innerHTML = scoped
    doc.body.append(newStyle)
  })

  const result = doc.body.innerHTML

  if (!wrap) return result

  if (Array.isArray(scope)) {
    throw new TypeError(
      'Cannot use multiple scopes when wrapping. Try replacing the array with a single string like ".myscope"'
    )
  }

  const wrapper = doc.createElement("div")
  wrapper.classList.add(scope)
  wrapper.innerHTML = result

  return wrapper.outerHTML
}

/**
 * Use a block list of elements to validate HTML string
 * @param {string} htmlValue
 * @param {array} block - Optional list of elements to block
 * @returns {string} - Empty string when there is no error, otherwise an error string
 */
export function findBadMarkup(htmlValue, block = BLOCKED_ELEMENTS) {
  const regex = new RegExp(`<(${block.join("|")}).*(\\/|<\\/\\w+)?>`, "i")
  const elementMatch = htmlValue.match(regex)
  if (elementMatch && block.includes(elementMatch[1])) {
    return `${elementMatch[1]} is not a supported element`
  }
  return ""
}

/**
 * Checks for bad paths in src and href values of HTML
 * @param {string} htmlValue
 * @returns {string} Empty string when there is no error, otherwise an error string
 */
export function findBadPaths(htmlValue) {
  const badPaths = htmlValue.match(
    /(src(?:set)?|href)=["'](?!(?:https?:|ftp:)?\/\/|mailto:|\{[%{]|#)(\S+)["']/i
  )
  if (badPaths) {
    // eslint-disable-next-line no-unused-vars
    const [_, attribute, value] = badPaths
    return `Unsupported value "${value}" passed to ${attribute}`
  }
  return ""
}

/**
 * Checks for bad attributes like on* events
 * @param {string} htmlValue
 * @param {array} block - Optional list of attributes to block. on* event attributes are blocked by default
 * @returns {string} Empty string when there is no error, otherwise an error string
 *
 * @example
 * checkAttributeName('<div onclick="alert(1)">') // "Unsupported attribute "onclick" with value "alert(1)"
 */
export function findBadAttributeName(htmlValue, block = BLOCKED_ATTRIBUTES) {
  const regex = new RegExp(
    `(?<=<[^>]*)(?<=\\s)(${block.join("|")})=["']?(.*?)["']?(?=[\n>])`,
    "i"
  )
  const badAttributes = htmlValue.match(regex)

  if (badAttributes) {
    // eslint-disable-next-line no-unused-vars
    const [_, attribute, value] = badAttributes
    return `Unsupported attribute "${attribute}" with value: ${value}`
  }
}

export function getHTMLErrors(htmlValue) {
  return (
    findBadPaths(htmlValue) ||
    findBadMarkup(htmlValue) ||
    findBadAttributeName(htmlValue)
  )
}

/**
 * HTMLEditor
 * - Uses a allow/block list to show only accepted html
 * - Removes faulty src paths like referencing a local file
 * @param {import("react").ElementType} as
 * @param {array[str]} allow list of elements to allow
 * @param {array[str]} block list of elements not to allow
 * @param {function} onChange
 */
function HTMLEditorComponent(
  { as: Component = "textarea", onChange = () => null, ...props },
  ref
) {
  const handleChange = event => {
    const { value } = event.target
    const error = getHTMLErrors(value)
    onChange({ value, error })
  }

  return <Component onChange={handleChange} ref={ref} {...props} />
}

export const HTMLEditor = React.forwardRef(HTMLEditorComponent)
